/** My apps — everything an organization installed: status, plan, usage,
 *  bills, people, cancel / renew, and its organization wallet. */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { topupOrgWallet } from '@/api/billing'
import {
  cancelInstall, changePlan, exportUrl, fmtDate, getApp, getInstall, installInvoiceUrl,
  invitePerson, keepInstall, listInstalls, money, mktError, resumeInstall, retryInstall,
  type Basis, type Subscription,
} from '@/api/marketplace'
import { useRazorpayCheckout } from '@/hooks/useRazorpayCheckout'
import MarketplaceShell, { AppTile, Stepper } from './MarketplaceShell'

type Panel = null | 'plan' | 'cancel' | 'topup'

const STATUS: Record<Subscription['status'], { label: string; cls: string }> = {
  installing: { label: 'Setting up', cls: 'info' },
  failed: { label: "Couldn't install", cls: 'bad' },
  trial: { label: 'Free trial', cls: 'info' },
  active: { label: 'Active', cls: 'ok' },
  past_due: { label: 'Payment needed', cls: 'bad' },
  ended: { label: 'Ended', cls: '' },
  deleted: { label: 'Removed', cls: '' },
}

export default function MyAppsPage() {
  const [data, setData] = useState<Awaited<ReturnType<typeof listInstalls>> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<number | null>(null)
  const [detail, setDetail] = useState<Subscription | null>(null)

  const load = useCallback(() => {
    listInstalls()
      .then((d) => {
        setData(d)
        setSelected((cur) => cur ?? d.items[0]?.id ?? null)
      })
      .catch((e) => setError(mktError(e, "We couldn't load your apps.").message))
  }, [])
  useEffect(load, [load])

  const loadDetail = useCallback(() => {
    if (selected == null) { setDetail(null); return }
    getInstall(selected).then(setDetail).catch((e) => toast.error(mktError(e).message))
  }, [selected])
  useEffect(loadDetail, [loadDetail])

  // Keep an installing app fresh.
  useEffect(() => {
    if (detail?.status !== 'installing') return
    const t = window.setTimeout(loadDetail, 1500)
    return () => window.clearTimeout(t)
  }, [detail, loadDetail])

  const refresh = (s?: Subscription) => { if (s) setDetail(s); load() }

  return (
    <MarketplaceShell>
      <div className="mkt-wrap mkt-split" style={{ padding: '24px 20px 48px' }}>
        <div className="mkt-main">
          <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
            <h1 style={{ fontSize: 28 }}>My apps</h1>
            <Link to="/marketplace" className="btn btn-secondary">Find more apps</Link>
          </div>
          {error && <div className="mkt-banner bad"><div>{error}</div></div>}
          {data && data.role === null && (
            <div className="mkt-banner info"><div><strong>Apps are managed by your organization's administrators</strong>Ask an administrator (or a project owner) to install or change apps for your organization.</div></div>
          )}
          {data && data.role !== null && data.items.length === 0 && (
            <div className="mkt-empty"><strong style={{ fontSize: 18 }}>No apps yet</strong><span className="mkt-muted">Apps you install for your organization appear here.</span><Link to="/marketplace" className="btn btn-primary">Explore apps</Link></div>
          )}
          {data && data.items.length > 0 && (
            <div role="tablist" aria-label="Installed apps" className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
              {data.items.map((it) => (
                <button key={it.id} type="button" role="tab" aria-selected={selected === it.id} onClick={() => setSelected(it.id)}
                  className={`mkt-choice mkt-choice-row${selected === it.id ? ' on' : ''}`} style={{ justifyContent: 'flex-start', gap: 12 }}>
                  <AppTile color={it.icon_color} text={it.initials} size={44} />
                  <span className="mkt-col" style={{ gap: 4 }}>
                    <strong>{it.name}</strong>
                    <span className={`mkt-pill ${STATUS[it.status].cls}`}>{STATUS[it.status].label}{it.status === 'trial' && it.trial_ends_at ? ` · until ${fmtDate(it.trial_ends_at)}` : ''}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {detail && <AppDetailPanel sub={detail} onChanged={refresh} />}
        </div>

        {data && data.items.length > 0 && (
          <aside className="mkt-aside">
            <div className="mkt-kpi">
              <span className="mkt-muted">Total each month</span>
              {Object.entries(data.monthly_total).length ? Object.entries(data.monthly_total).map(([c, v]) => (
                <strong key={c}>{money(v, c)}</strong>
              )) : <strong>{money(0, data.items[0].currency)}</strong>}
              <span className="mkt-faint">+ GST, for running apps</span>
            </div>
            {detail && <WalletCard sub={detail} onPaid={loadDetail} />}
          </aside>
        )}
      </div>
    </MarketplaceShell>
  )
}

function WalletCard({ sub, onPaid }: { sub: Subscription; onPaid: () => void }) {
  const { checkout, busy } = useRazorpayCheckout()
  const [amount, setAmount] = useState(Math.max(sub.monthly_minor, 10000) / 100)
  useEffect(() => setAmount(Math.max(sub.monthly_minor, 10000) / 100), [sub.id, sub.monthly_minor])
  const pay = async () => {
    const minor = Math.round(amount * 100)
    if (minor <= 0) return
    const r = await checkout({ purpose: 'org_wallet_topup', description: 'Organization wallet top-up', precreate: () => topupOrgWallet(minor, sub.currency) })
    if (r.cancelled) return
    if (!r.ok) { toast.error(r.error || 'The payment did not go through.'); return }
    toast.success('Money added to your organization wallet')
    onPaid()
  }
  return (
    <div className="mkt-panel mkt-col" style={{ gap: 10 }}>
      <span className="mkt-muted">Organization wallet</span>
      <strong style={{ fontSize: 26 }}>{money(sub.wallet_balance_minor, sub.currency)}</strong>
      <span className="mkt-faint">Every installed app's monthly price is taken from here.</span>
      <div className="mkt-field">
        <label htmlFor="topup">Add money</label>
        <div className="mkt-money"><span>{sub.currency === 'INR' ? '₹' : '$'}</span><input id="topup" inputMode="decimal" value={amount} onChange={(e) => setAmount(Number(e.target.value.replace(/[^\d.]/g, '')) || 0)} /></div>
      </div>
      <button type="button" className="btn btn-primary" onClick={pay} disabled={busy || amount <= 0}>{busy ? 'Opening payment…' : 'Add with UPI or card'}</button>
      <span className="mkt-faint">GST is added at payment.</span>
    </div>
  )
}

function AppDetailPanel({ sub, onChanged }: { sub: Subscription; onChanged: (s?: Subscription) => void }) {
  const [panel, setPanel] = useState<Panel>(null)
  const [busy, setBusy] = useState(false)
  const [invite, setInvite] = useState('')
  const act = async (fn: () => Promise<Subscription | { subscription: Subscription }>, ok: string) => {
    setBusy(true)
    try {
      const r = await fn()
      onChanged('subscription' in r ? r.subscription : r)
      toast.success(ok)
      setPanel(null)
    } catch (e) {
      toast.error(mktError(e).message)
    } finally {
      setBusy(false)
    }
  }

  const banner = bannerFor(sub)
  const running = ['trial', 'active', 'past_due'].includes(sub.status)
  return (
    <>
      {banner && (
        <div className={`mkt-banner ${banner.cls}`} role="status">
          <div><strong>{banner.title}</strong>{banner.text}</div>
          {sub.status === 'ended' && sub.can_resume && <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act(() => resumeInstall(sub.id), 'Welcome back — the app is running again')}>Renew subscription</button>}
          {sub.status === 'failed' && <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act(() => retryInstall(sub.id), 'Trying again…')}>Try again</button>}
          {sub.cancel_at_period_end && <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act(() => keepInstall(sub.id), "Your plan continues")}>Keep my plan</button>}
        </div>
      )}

      <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        <section className="mkt-panel mkt-col">
          <div className="mkt-row" style={{ justifyContent: 'space-between' }}><h2>Your plan</h2>{sub.listing_slug && <Link to={`/marketplace/app/${sub.listing_slug}`} style={{ fontSize: 14 }}>See what's included</Link>}</div>
          <div className="mkt-col" style={{ gap: 2 }}>
            <strong style={{ fontSize: 22 }}>{sub.basis === 'person' ? `${sub.seats} people · per person` : `${sub.size_name} server`}</strong>
            <span className="mkt-muted">{sub.basis === 'person' ? `${money(sub.unit_price_minor, sub.currency)} each · ` : ''}{money(sub.monthly_minor, sub.currency)} a month + GST</span>
          </div>
          <div className="mkt-col" style={{ gap: 6, borderTop: '1px solid var(--color-border)', paddingTop: 10, fontSize: 14 }}>
            <Row k="Next payment" v={sub.next_charge_at ? `${money(sub.monthly_minor, sub.currency)} on ${fmtDate(sub.next_charge_at)}` : sub.cancel_at_period_end ? 'None — cancelled' : '—'} />
            <Row k="Paid from" v="Organization wallet" />
            {sub.pending.seats != null && <Row k="From next period" v={`${sub.pending.seats} people`} />}
            {sub.pending.size && <Row k="From next period" v={`${sub.pending.size} server`} />}
            {sub.pending.price_minor != null && <Row k="New price" v={`${money(sub.pending.price_minor, sub.currency)} from ${fmtDate(sub.pending.price_effective_at)}`} />}
          </div>
          {running && (
            <div className="mkt-row">
              <a href={sub.open_url} className="btn btn-primary" target="_blank" rel="noreferrer">Open app</a>
              {sub.status !== 'past_due' && <button type="button" className="btn btn-secondary" onClick={() => setPanel(panel === 'plan' ? null : 'plan')}>Change plan</button>}
            </div>
          )}
        </section>

        {sub.usage && (
          <section className="mkt-panel mkt-col">
            <div className="mkt-row" style={{ justifyContent: 'space-between' }}><h2>How much you're using</h2><span className="mkt-faint">This month</span></div>
            {sub.usage.map((u) => (
              <div key={u.key} className="mkt-col" style={{ gap: 5 }}>
                <div className="mkt-row" style={{ justifyContent: 'space-between', fontSize: 14 }}>
                  <span>{u.label}</span>
                  <span><strong>{u.used == null ? 'Not measured yet' : `${u.used}${u.unit ? ` ${u.unit}` : ''}`}</strong>{u.of != null && u.used != null ? ` of ${u.of}${u.unit ? ` ${u.unit}` : ''}` : ''}</span>
                </div>
                {u.pct != null && <div className="mkt-meter" role="img" aria-label={`${u.label}: ${u.pct}%`}><div className={u.pct >= 80 ? 'warn' : ''} style={{ width: `${Math.min(100, u.pct)}%` }} /></div>}
                {u.pct != null && u.pct >= 80 && <span className="mkt-faint" style={{ color: 'var(--color-warning)' }}>Getting close — think about a bigger plan.</span>}
              </div>
            ))}
          </section>
        )}
      </div>

      {panel === 'plan' && <PlanPanel sub={sub} busy={busy} onClose={() => setPanel(null)} onSave={(b, seats, size) => act(() => changePlan(sub.id, { basis: b, seats, size }), 'Plan updated')} />}

      {running && (
        <section className="mkt-panel mkt-col">
          <h2>People</h2>
          <span className="mkt-muted" style={{ fontSize: 14 }}>{sub.people ?? 0} of {sub.basis === 'person' ? (sub.status === 'trial' ? Math.min(sub.seats ?? 0, sub.trial_people ?? 0) : sub.seats) : sub.limits?.find((l) => l.key === 'people')?.value} places used</span>
          <form className="mkt-row" style={{ flexWrap: 'nowrap' }} onSubmit={(e) => { e.preventDefault(); if (invite.trim()) act(() => invitePerson(sub.id, invite.trim()), 'Invitation sent').then(() => setInvite('')) }}>
            <label htmlFor="inv2" className="mkt-sr">Email to invite</label>
            <input id="inv2" type="email" value={invite} onChange={(e) => setInvite(e.target.value)} placeholder="name@yourcompany.com" style={{ flex: 1, font: 'inherit', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--color-border-strong, var(--color-border))', background: 'var(--color-input-bg, var(--color-bg))', color: 'var(--color-text)' }} />
            <button type="submit" className="btn btn-primary" disabled={busy}>Invite</button>
          </form>
          <span className="mkt-faint">Remove people or change their roles from inside the app (Users).</span>
        </section>
      )}

      {sub.charges && (
        <section className="mkt-panel mkt-col">
          <h2>Bills &amp; invoices</h2>
          {sub.charges.length === 0 ? <span className="mkt-muted">No bills yet{sub.status === 'trial' ? " — you're on your free trial" : ''}.</span> : (
            <div className="mkt-scroll">
              <table className="mkt-table">
                <thead><tr><th scope="col">Date</th><th scope="col">What for</th><th scope="col" className="num">Amount</th><th scope="col"><span className="mkt-sr">Invoice</span></th></tr></thead>
                <tbody>
                  {sub.charges.map((c) => (
                    <tr key={c.id}>
                      <td>{fmtDate(c.date)}</td><td>{c.description}</td><td className="num">{money(c.amount_minor, sub.currency)}</td>
                      <td className="num">{c.invoice_number && <a href={installInvoiceUrl(sub.id, c.invoice_number)} target="_blank" rel="noreferrer">Invoice</a>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="mkt-panel mkt-col">
        <h2>Manage</h2>
        <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          {sub.can_export && <a className="mkt-choice" href={exportUrl(sub.id)}><strong>Download all your data</strong><span className="mkt-muted" style={{ fontSize: 14 }}>A spreadsheet of every record, zipped.</span></a>}
          {sub.support_email && <a className="mkt-choice" href={`mailto:${sub.support_email}`}><strong>Get help</strong><span className="mkt-muted" style={{ fontSize: 14 }}>Message {sub.publisher}.</span></a>}
        </div>
        {running && !sub.cancel_at_period_end && (
          <div className="mkt-row" style={{ justifyContent: 'space-between', borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
            <span className="mkt-muted">No longer need {sub.name}?</span>
            <button type="button" className="btn btn-danger" onClick={() => setPanel('cancel')}>Cancel subscription</button>
          </div>
        )}
      </section>

      {panel === 'cancel' && <CancelPanel sub={sub} busy={busy} onClose={() => setPanel(null)} onConfirm={(reason) => act(() => cancelInstall(sub.id, reason), sub.status === 'trial' ? 'Trial ended' : 'Cancelled at the end of this period')} />}
    </>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">{k}</span><span>{v}</span></div>
}

function bannerFor(sub: Subscription): { cls: string; title: string; text: string } | null {
  switch (sub.status) {
    case 'installing': return { cls: 'info', title: 'Setting up…', text: "This usually takes under a minute. We'll email you when it's ready." }
    case 'failed': return { cls: 'bad', title: "We couldn't finish the install", text: sub.install.error || 'Nothing was charged.' }
    case 'trial': return { cls: 'info', title: `Your free trial ends on ${fmtDate(sub.trial_ends_at)}`, text: `Then ${money(sub.monthly_minor, sub.currency)} + GST is taken from your organization wallet each month.${sub.wallet_balance_minor < sub.monthly_minor ? ` Your wallet needs ${money(sub.monthly_minor - sub.wallet_balance_minor, sub.currency)} more by then.` : ''}` }
    case 'past_due': return { cls: 'bad', title: "We couldn't take this month's payment", text: `Your organization wallet is short. ${sub.name} keeps working until ${fmtDate(sub.grace_until)} — add money and we'll take the payment automatically.` }
    case 'active':
      return sub.cancel_at_period_end
        ? { cls: 'plain', title: 'Cancelled', text: `${sub.name} keeps working until ${fmtDate(sub.period_end)} and won't be charged again.` }
        : null
    case 'ended': return { cls: 'plain', title: `${sub.name} has ended`, text: `${sub.can_export ? `You can download your data until ${fmtDate(sub.readonly_until)}. ` : ''}Renew any time before ${fmtDate(sub.delete_at)}; after that your data is deleted for good.` }
    case 'deleted': return { cls: 'plain', title: 'Removed', text: `Your data was deleted on ${fmtDate(sub.deleted_at)}.${sub.delete_note ? ` ${sub.delete_note}` : ''}` }
    default: return null
  }
}

function PlanPanel({ sub, busy, onClose, onSave }: { sub: Subscription; busy: boolean; onClose: () => void; onSave: (b: Basis, seats: number | null, size: string | null) => void }) {
  const [basis, setBasis] = useState<Basis>(sub.basis)
  const [seats, setSeats] = useState(sub.seats ?? 10)
  const [size, setSize] = useState<string | null>(sub.size)
  const [offer, setOffer] = useState<{ person: number | null; server: { id: string; name: string; max_people: number; price_minor: number }[] } | null>(null)
  useEffect(() => {
    if (sub.listing_slug) getApp(sub.listing_slug).then((a) => setOffer({ person: a.person?.price_minor ?? null, server: a.server })).catch(() => setOffer(null))
  }, [sub.listing_slug])
  const unit = basis === sub.basis && (basis === 'person' || size === sub.size) ? sub.unit_price_minor
    : basis === 'person' ? offer?.person ?? 0 : offer?.server.find((s) => s.id === size)?.price_minor ?? 0
  const newMonthly = basis === 'person' ? unit * seats : unit
  const more = newMonthly > sub.monthly_minor
  return (
    <section className="mkt-panel mkt-panel-accent mkt-col" aria-label="Change plan">
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}><h2>Change your plan</h2><button type="button" className="btn btn-ghost" aria-label="Close" onClick={onClose}>×</button></div>
      {offer && offer.person != null && offer.server.length > 0 && (
        <div className="mkt-choices" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <button type="button" aria-pressed={basis === 'person'} className={`mkt-choice${basis === 'person' ? ' on' : ''}`} onClick={() => setBasis('person')}><strong>Per person</strong></button>
          <button type="button" aria-pressed={basis === 'server'} className={`mkt-choice${basis === 'server' ? ' on' : ''}`} onClick={() => { setBasis('server'); setSize(size || offer.server[0].id) }}><strong>Per server</strong></button>
        </div>
      )}
      {basis === 'person' ? (
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span>People using {sub.name}</span><Stepper value={seats} onChange={setSeats} label="people" /></div>
      ) : (
        <div className="mkt-choices">
          {(offer?.server || []).map((s) => (
            <button key={s.id} type="button" role="radio" aria-checked={size === s.id} onClick={() => setSize(s.id)} className={`mkt-choice mkt-choice-row${size === s.id ? ' on' : ''}`}>
              <span><strong>{s.name} server</strong> <span className="mkt-muted">· up to {s.max_people} people</span></span><strong>{money(s.price_minor, sub.currency)}</strong>
            </button>
          ))}
        </div>
      )}
      <div className="mkt-row" style={{ gap: 24 }}>
        <span>Now: <strong>{money(sub.monthly_minor, sub.currency)}</strong> a month</span>
        <span>New: <strong>{money(newMonthly, sub.currency)}</strong> a month</span>
      </div>
      <span className="mkt-muted" style={{ fontSize: 14 }}>
        {sub.status === 'trial' ? 'Changes apply straight away; nothing is charged during the trial.'
          : more ? `The difference (${money(newMonthly - sub.monthly_minor, sub.currency)}) is taken from your organization wallet now, for the rest of this month.`
            : newMonthly < sub.monthly_minor ? `The lower price starts with your next period on ${fmtDate(sub.period_end)}.` : 'No change in price.'}
      </span>
      <div className="mkt-row">
        <button type="button" className="btn btn-primary" disabled={busy || !unit} onClick={() => onSave(basis, basis === 'person' ? seats : null, basis === 'server' ? size : null)}>Save change</button>
        <button type="button" className="btn btn-secondary" onClick={onClose}>Not now</button>
      </div>
    </section>
  )
}

function CancelPanel({ sub, busy, onClose, onConfirm }: { sub: Subscription; busy: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('')
  const r = sub.retention
  const endDate = sub.status === 'trial' ? 'today' : fmtDate(sub.period_end)
  return (
    <section className="mkt-panel mkt-panel-danger mkt-col" aria-label="Cancel subscription">
      <h2>Cancel {sub.name}?</h2>
      <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <li>{sub.status === 'trial' ? `Your free trial ends now; nothing is charged.` : `${sub.name} keeps working until ${endDate}. You won't be charged again.`}</li>
        <li>After that your team can no longer sign in to it.</li>
        {r && <li>For {r.readonly_days} days after that you can still download everything from here.</li>}
        {r && <li>Your data is deleted for good {sub.status === 'trial' ? r.trial_data_days : r.delete_after_days} days after it ends. Renew any time before then to keep it.</li>}
      </ul>
      <fieldset style={{ border: 0, margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <legend style={{ fontWeight: 600, marginBottom: 6 }}>Mind telling us why? (optional)</legend>
        {['Too expensive', 'Missing something we need', 'We switched to another app', 'We no longer need it'].map((x) => (
          <label key={x} className="mkt-row" style={{ gap: 8, cursor: 'pointer' }}><input type="radio" name="why" checked={reason === x} onChange={() => setReason(x)} />{x}</label>
        ))}
      </fieldset>
      <div className="mkt-row">
        <button type="button" className="btn btn-primary" onClick={onClose}>Keep my plan</button>
        <button type="button" className="btn btn-danger" disabled={busy} onClick={() => onConfirm(reason)}>{sub.status === 'trial' ? 'End the trial' : 'Cancel at end of period'}</button>
      </div>
    </section>
  )
}
